<script lang="ts">
  import { worldHour, worldDay } from '../worldTime'
  /** ITS DAY AS A RING (NPC life, L1 — 10-23: the Chair's two reference frames — "Wakeup -> … -> Get dressed -> Breakfast -> …
   *  The dot in the graph is where the npc is in transit between states … image 8 … fills with options that the NPC can choose from").
   *  The world day as a clock face: each step of its ring an arc for its hours — at home, at work, on the way — the step it is on lit;
   *  THE DOT where the world clock is now (it moves as the day does); each decision today a small fan out of the ring at its hour — the
   *  options it weighed, the one it chose bright. A day lived on its ring leaves no fans: habit, nothing to decide. */
  import type { Decision, Routine } from '../types'
  let { routine, decisions = [], live = false }: { routine: Routine; decisions?: Decision[]; live?: boolean } = $props()

  const W = 640, S = 420, CX = W / 2, C = S / 2, R = 128
  // (the world's hour and day: worldTime.ts — the one clock; no rate here)
  const hourOf = worldHour, dayOf = worldDay
  let now = $state(Date.now())
  $effect(() => { const iv = setInterval(() => (now = Date.now()), 1000); return () => clearInterval(iv) })
  const wh = $derived(hourOf(now))
  const ang = (h: number) => (h / 24) * Math.PI * 2 - Math.PI / 2
  const pt = (h: number, r: number) => [CX + Math.cos(ang(h)) * r, C + Math.sin(ang(h)) * r] as const
  /** a step's name as people read it (the ring's second Work is "Work 2" inside — "Work" here) */
  const label = (n: string) => n.replace(/ \d+$/, '')
  const arc = (f: number, t: number, r: number) => {
    const [x0, y0] = pt(f, r), [x1, y1] = pt(Math.max(f + 0.01, t - 0.02), r), big = t - f > 12 ? 1 : 0
    return `M${x0.toFixed(1)},${y0.toFixed(1)} A${r},${r} 0 ${big} 1 ${x1.toFixed(1)},${y1.toFixed(1)}`
  }
  const WHERE: Record<string, { c: string; label: string }> = { home: { c: '#19c37d', label: 'at home' }, work: { c: '#9b5cff', label: 'at work' }, away: { c: '#5d7684', label: 'on the way · out' } }
  const hm = (h: number) => `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.floor((h % 1) * 60)).padStart(2, '0')}`
  const steps = $derived(routine.steps.map((s) => ({ ...s, now: s.n === routine.step, mid: (s.f + s.t) / 2, long: s.t - s.f >= 0.45 })))
  const cur = $derived(steps.find((s) => s.now) ?? null)
  const clockStep = $derived(steps.find((s) => wh >= s.f && wh < s.t) ?? null)
  const today = $derived(dayOf(now) === routine.day)

  // the fans: today's decisions, each at its hour — up to six options weighed, the chosen one bright
  const DRIVE: Record<string, string> = { energy: '#f5c518', hunger: '#ff5a5f', hygiene: '#22d3ee', fun: '#e535ab', social: '#ff6fd8', comfort: '#3b6dff' }
  const fans = $derived(decisions.filter((d) => dayOf(Date.parse(d.at)) === routine.day).map((d) => {
    const h = hourOf(Date.parse(d.at)), scored = d.branches.filter((b) => b.score != null).slice(0, 6), top = Math.max(0.01, ...scored.map((b) => b.score ?? 0))
    const [x0, y0] = pt(h, R + 6)
    const strands = scored.map((b, i) => {
      const spread = (i - (scored.length - 1) / 2) * 0.32, len = 24 + 46 * ((b.score ?? 0) / top), a = ang(h) + spread * 0.5
      const lead = b.parts?.length ? b.parts.reduce((m, p) => (p.value > m.value ? p : m)).drive : null
      return { x: x0 + Math.cos(a) * len, y: y0 + Math.sin(a) * len, c: lead ? DRIVE[lead] ?? '#c9a227' : '#5d7684', chosen: b.name === d.chose, title: `${b.name} ${(b.score ?? 0).toFixed(2)}${b.name === d.chose ? ' — chosen' : ''}` }
    })
    return { at: d.at, x0, y0, strands, chose: d.chose, h }
  }))
</script>

<div class="ring">
  <svg viewBox={`0 0 ${W} ${S}`} role="img" aria-label={`Its day as a ring — ${cur ? `on ${cur.n}` : 'not on its ring'}, ${fans.length} decision(s) today`}>
    <defs><filter id="rg-glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter></defs>
    <circle cx={CX} cy={C} r={R} class="track" />
    {#each [0, 6, 12, 18] as h}{@const [x, y] = pt(h, R - 18)}<text {x} y={y + 3} class="hr">{String(h).padStart(2, '0')}</text>{/each}
    {#each steps as s (s.n)}
      {@const [tx, ty] = pt(s.f, R)}
      <path d={arc(s.f, s.t, R)} stroke={WHERE[s.w]?.c ?? '#5d7684'} class="seg" class:away={s.w === 'away'} class:now={s.now} filter={s.now ? 'url(#rg-glow)' : undefined}>
        <title>{s.n}{s.o ? ` — ${s.o}` : ''} · {hm(s.f)}–{hm(s.t)} · {WHERE[s.w]?.label}{s.now ? ' — now' : ''}</title>
      </path>
      <circle cx={tx} cy={ty} r="1.6" class="tick" />
      {#if s.long || s.now}
        {@const [lx, ly] = pt(s.mid, R + 20)}
        <text x={lx} y={ly + 3} class="lbl" class:now={s.now} text-anchor={Math.cos(ang(s.mid)) > 0.25 ? 'start' : Math.cos(ang(s.mid)) < -0.25 ? 'end' : 'middle'}>{label(s.n)}</text>
      {/if}
    {/each}
    {#each fans as f (f.at)}
      <g class="fan">
        {#each f.strands as st}<line x1={f.x0} y1={f.y0} x2={st.x} y2={st.y} stroke={st.c} class:chosen={st.chosen}><title>{st.title}</title></line>{/each}
        <circle cx={f.x0} cy={f.y0} r="3.2" class="fnode"><title>{hm(f.h)} — {f.chose ? `chose ${f.chose}` : 'chose nothing — back on its ring'}</title></circle>
      </g>
    {/each}
    {#if today}
      {@const [dx, dy] = pt(wh, R)}
      <circle cx={dx} cy={dy} r="9" class="halo" /><circle cx={dx} cy={dy} r="4.6" class="dot"><title>The world clock — {hm(wh)}</title></circle>
    {/if}
    <text x={CX} y={C - 14} class="cnow mono">{hm(wh)}</text>
    <text x={CX} y={C + 6} class="cstep">{cur ? label(cur.n) : '—'}</text>
    <text x={CX} y={C + 22} class="cwhere">{cur ? `${WHERE[cur.w]?.label}${cur.o ? ' · ' + cur.o : ''}` : 'not on its ring'}</text>
    {#if cur && clockStep && clockStep.n !== cur.n}<text x={CX} y={C + 36} class="cwhere lag">the clock says {label(clockStep.n)}</text>{/if}
  </svg>
  <div class="rbar mono">
    {#each Object.entries(WHERE) as [k, v]}<span><i style={`background:${v.c}`}></i>{v.label}</span>{/each}
    <span class="rt">{fans.length ? `${fans.length} decision${fans.length > 1 ? 's' : ''} today` : 'no decisions today — habit'}</span>
    <span class:live>{live ? '● live' : '○ not live'}</span>
  </div>
</div>

<style>
  .ring { background: #0a1014; border: 1px solid var(--line-dim); }
  svg { width: 100%; max-width: 760px; display: block; margin: 0 auto; }
  .track { fill: none; stroke: #13232b; stroke-width: 16; }
  .seg { fill: none; stroke-width: 12; opacity: .55; stroke-linecap: butt; }
  .seg.away { stroke-dasharray: 3 3; opacity: .45; }
  .seg.now { opacity: 1; stroke-width: 16; }
  .tick { fill: #0a1014; }
  .hr { font: 8.5px var(--mono); fill: var(--faint); text-anchor: middle; }
  .lbl { font: 9.5px var(--mono); fill: #9db3c0; }
  .lbl.now { fill: #fff; font-weight: 700; }
  .fan line { stroke-width: 1.1; opacity: .55; stroke-linecap: round; }
  .fan line.chosen { stroke-width: 3; opacity: 1; }
  .fnode { fill: #fff; }
  .halo { fill: #22d3ee; opacity: .25; }
  .dot { fill: #22d3ee; stroke: #e6fbff; stroke-width: 1.2; }
  .cnow { font-size: 18px; fill: #e6f1f6; text-anchor: middle; }
  .cstep { font: 700 12px var(--display); fill: #9fe8f5; text-anchor: middle; letter-spacing: .03em; }
  .cwhere { font: 9.5px var(--mono); fill: var(--muted); text-anchor: middle; }
  .cwhere.lag { fill: #c9a227; }
  .rbar { display: flex; gap: 6px 14px; flex-wrap: wrap; align-items: center; padding: 6px 10px; border-top: 1px solid var(--line-dim); font-size: 11px; color: var(--muted); }
  .rbar i { display: inline-block; width: 14px; height: 3px; margin-right: 5px; vertical-align: middle; }
  .rt { margin-left: auto; }
  .live { color: var(--green); }
</style>
